"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = TimeAttendanceCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const cancelable_promise_1 = require("cancelable-promise");
const react_native_1 = require("react-native");
const react_i18next_1 = require("react-i18next");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const Theme_1 = require("../Theme");
const Helper_1 = require("../Helper");
const TimeAttendance_1 = __importDefault(require("../api/TimeAttendance"));
const UserContext_1 = require("../contexts/UserContext");
const Card_1 = __importDefault(require("../components/Card"));
const MainMenuItem_1 = __importDefault(require("../components/MainMenuItem"));
const MainMenuButton_1 = __importDefault(require("../components/MainMenuButton"));
const Loading_1 = __importDefault(require("../components/Loading"));
const PtoBalanceSummaryBreakdown_1 = __importDefault(require("./PtoBalanceSummaryBreakdown"));
const QuickPunch_1 = __importDefault(require("../shared/QuickPunch"));
const FirebaseAnalytics_1 = __importDefault(require("../shared/FirebaseAnalytics"));
const ThemeContext_1 = require("../ThemeContext");
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const FadeInView_1 = __importDefault(require("../components/FadeInView"));
const SingleSignOn_1 = require("../shared/SingleSignOn");
const MaintenanceContext_1 = require("../contexts/MaintenanceContext");
const useStyles = () => {
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    return react_native_1.StyleSheet.create({
        headText: {
            color: THEME.Text,
            fontSize: size != 'sm' ? 14 : 12,
        },
        numberText: {
            color: THEME.Text,
            fontSize: 14
        },
        numberValue: {
            color: THEME.Primary,
            fontSize: size != 'sm' ? 24 : 22,
            fontWeight: '500'
        },
        empty: {
            alignItems: 'center',
            backgroundColor: size != 'sm' ? THEME.UltraLightGray : THEME.SoftGray,
            borderRadius: 4,
            flex: 1,
            flexDirection: 'row',
            justifyContent: 'center',
            margin: 3,
            padding: 8
        },
        button: {
            backgroundColor: THEME.CardBackground,
            borderColor: THEME.CardBorder,
            borderWidth: 1,
            borderBottomWidth: 2,
            borderRadius: 4,
            margin: 3
        },
    });
};
const TimeClock = (props) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const styles = useStyles();
    const THEME = (0, ThemeContext_1.useTheme)();
    const STYLES = (0, Theme_1.useGlobalStyles)();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const [timeAndAttendance] = react_1.default.useState(userContext.client?.features?.find((feature) => feature.name === 'time-and-attendance'));
    const [employeeTimeclockEnabled] = react_1.default.useState(userContext.employee?.clients?.[userContext.client.id]?.usingTimeclock);
    const useTimeclockOption = timeAndAttendance?.useTimeclockOption || (timeAndAttendance?.useTimeclock ? 'AllEmployees' : 'No');
    const employeeCanUseTimeclock = employeeTimeclockEnabled !== false;
    const employeeIsNonExempt = userContext.employee?.clients?.[userContext.client.id]?.flsaExempt === false;
    const showTimeclock = employeeCanUseTimeclock &&
        (useTimeclockOption === 'AllEmployees' ||
            (useTimeclockOption === 'NonExemptEmployeesOnly' &&
                employeeIsNonExempt));
    const timer = react_1.default.useRef(null);
    const [clocked, setClocked] = react_1.default.useState(null);
    const [disablePunch, setDisablePunch] = react_1.default.useState(false);
    const [time, setTime] = react_1.default.useState(null);
    const [missed, setMissed] = react_1.default.useState(false);
    const [meridian, setMeridian] = react_1.default.useState("");
    const promise = react_1.default.useRef(null);
    const submitPunch = react_1.default.useCallback((punchType) => {
        Helper_1.Helper.verifyLocationPermissions().then(_ => {
            FirebaseAnalytics_1.default.LogEvent('feature_used', {
                'feature': punchType == 'PUNCH_IN' ? 'Clock In' : 'Clock Out',
                'client': userContext.client?.id,
                'employee': userContext.employee?.employeeId
            });
            QuickPunch_1.default.punch(punchType, (timestamp) => setClocked(timestamp), (disable) => setDisablePunch(disable), promise, userContext, (translationKey) => t(translationKey));
        });
    }, [clocked]);
    react_1.default.useEffect(() => {
        refresh();
        timer.current = setInterval(() => {
            let d = new Date();
            if (d.getHours() < 12)
                setMeridian("am");
            else
                setMeridian("pm");
            setTime(d.toLocaleTimeString().replace("AM", "").replace("PM", "").trim());
        }, 200);
        return () => {
            if (timer.current) {
                clearInterval(timer.current);
            }
            promise.current?.cancel();
        };
    }, []);
    const refresh = react_1.default.useCallback(() => {
        if (showTimeclock) {
            promise.current?.cancel();
            promise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => {
                TimeAttendance_1.default.getLastPunch(token).then(data => {
                    if (data.activity == "PUNCH_IN") {
                        setClocked(data.timestamp);
                    }
                    // isMissed doesn't exist on the return type and hasn't for some time
                    //setMissed(data.isMissed);
                });
            }).catch(err => {
                console.error(err);
            }));
        }
    }, [showTimeclock]);
    return ((showTimeclock &&
        (0, jsx_runtime_1.jsx)(react_native_1.View, { style: [styles.button, STYLES.notSelectable, { marginTop: 3, borderTopRightRadius: 4, borderTopLeftRadius: 4 }], children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', alignContent: 'center', alignItems: 'center' }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.Pressable, { onPress: () => submitPunch("PUNCH_IN"), style: ({ pressed }) => [(pressed ? STYLES.pressed : null), { flex: 1, alignItems: 'center', borderWidth: 1, borderBottomWidth: 2, borderRadius: 4, borderColor: THEME.CardBackground, padding: 8, paddingVertical: 12 }, (pressed ? { borderColor: THEME.Secondary } : null)], disabled: disablePunch, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.ArrowRightToBracket, size: 14, color: THEME.Primary, style: { marginBottom: 2 } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { marginTop: 2, color: THEME.Primary, fontSize: 14, fontWeight: '500' }, children: t('time:buttons.clockIn') })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { justifyContent: 'center', alignItems: 'center', flex: 1, padding: 8 }, children: [(0, jsx_runtime_1.jsxs)(react_native_1.Text, { style: { fontSize: size != 'sm' ? 24 : 20, textAlign: 'center', color: THEME.Text, fontWeight: '300' }, children: [time, (0, jsx_runtime_1.jsxs)(react_native_1.Text, { style: { fontSize: 12 }, children: ["\u00A0", meridian] })] }), clocked != null &&
                                (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontSize: 11, textAlign: 'center', color: THEME.TextLight }, children: t("time:text.clocked") + '\r\n' + new Date(clocked).toLocaleTimeString() }), missed &&
                                (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { fontSize: 11, textAlign: 'center', color: THEME.ErrorTagText, fontWeight: '300' }, children: t("time:text.missedPunch") })] }), (0, jsx_runtime_1.jsxs)(react_native_1.Pressable, { onPress: () => submitPunch("PUNCH_OUT"), style: ({ pressed }) => [(pressed ? STYLES.pressed : null), { flex: 1, alignItems: 'center', borderWidth: 1, borderBottomWidth: 2, borderRadius: 4, borderColor: THEME.CardBackground, padding: 8, paddingVertical: 12 }, (pressed ? { borderColor: THEME.Secondary } : null)], disabled: disablePunch, children: [(0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.ArrowRightFromBracket, size: 14, color: THEME.Primary, style: { marginBottom: 2 } }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { marginTop: 2, color: THEME.Primary, fontSize: 14, fontWeight: '500' }, children: t('time:buttons.clockOut') })] })] }) })));
};
const PaidTimeOff = (props) => {
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const vendor = props.vendor ?? userContext.client?.features?.find((feature) => feature.name === 'time-and-attendance')?.vendor?.toLowerCase();
    const isPrismHr = vendor === 'prismhr';
    const promise = react_1.default.useRef(null);
    const [status, setStatus] = react_1.default.useState(null);
    const [balances, setBalances] = react_1.default.useState(null);
    const [selectedBalance, setSelectedBalance] = react_1.default.useState(null);
    const [sheetVisible, setSheetVisible] = react_1.default.useState(false);
    const { t } = (0, react_i18next_1.useTranslation)();
    const styles = useStyles();
    const THEME = (0, ThemeContext_1.useTheme)();
    const STYLES = (0, Theme_1.useGlobalStyles)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const refresh = react_1.default.useCallback(() => {
        promise.current?.cancel();
        setStatus('loading');
        promise.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => TimeAttendance_1.default.getPtoBalances(token))).then(data => {
            if (data?.balances && data.balances.length > 0) {
                const enriched = data.balances.map((item) => ({
                    ...item,
                    earned: item.earned ?? item.accrued ?? '0.00',
                    carryover: item.carryover ?? '0.00',
                    taken: item.taken ?? '0.00',
                    scheduled: item.scheduled ?? '0.00',
                    remaining: item.remaining ?? '0.00',
                }));
                setBalances(enriched);
            }
            setStatus('done');
        }).catch(e => {
            setBalances(null);
            setStatus((e == 404) ? 'done' : 'error');
        });
    }, []);
    react_1.default.useEffect(() => {
        if (!props.disabled) {
            refresh();
        }
        return () => promise.current?.cancel();
    }, [props.disabled]);
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { children: [(status == 'done')
                &&
                    (0, jsx_runtime_1.jsx)(FadeInView_1.default, { children: (balances != null && balances?.length > 0)
                            &&
                                (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flexDirection: 'row', flexWrap: 'wrap', flex: 1 }, children: [balances.map((item) => (0, jsx_runtime_1.jsx)(react_native_1.View, { accessible: true, style: [STYLES.notSelectable, { flexBasis: '33.33%' }], children: (0, jsx_runtime_1.jsxs)(MainMenuButton_1.default, { disabled: props.disabled, onPress: () => {
                                                    if (isPrismHr) {
                                                        setSelectedBalance(item);
                                                        setSheetVisible(true);
                                                    }
                                                    else {
                                                        props.onOpenTimeAttendance?.();
                                                    }
                                                }, style: { flexDirection: 'column', margin: 3, justifyContent: 'flex-end', paddingBottom: size != 'sm' ? 14 : 10 }, children: [item.unlimited ? ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { height: size != 'sm' ? 36 : 30, justifyContent: 'center', alignItems: 'center', paddingTop: 8 }, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.Infinity, size: size != 'sm' ? 24 : 20, style: { overflow: 'visible' }, color: THEME.Primary }) })) : ((0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: [
                                                            styles.numberText,
                                                            styles.numberValue,
                                                        ], numberOfLines: 1, adjustsFontSizeToFit: true, children: item.remaining })), (0, jsx_runtime_1.jsx)(react_native_1.Text, { accessible: false, style: [
                                                            styles.numberText,
                                                            { textAlign: 'center', fontWeight: '500', marginTop: 2, marginBottom: 1 }
                                                        ], numberOfLines: 1, children: item.name }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.TextLight, fontSize: 10, textAlign: 'center' }, children: item.unlimited ? t('time:text.discretionaryUse') : t('time:text.hoursRemain') })] }) }, item.name)), (balances.length % 3 > 0) &&
                                            [...Array(3 - (balances.length % 3))].map((_, index) => (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flexBasis: '33.33%' }, children: (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.empty, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.AlarmClock, size: 36, color: THEME.TextDisabled }) }) }, 'pto-blank-' + index))] })
                            ||
                                (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flexDirection: 'row', justifyContent: 'center', flexWrap: 'wrap' }, children: [...Array(3)].map((_, index) => (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flexBasis: '33.33%' }, children: (0, jsx_runtime_1.jsx)(react_native_1.View, { style: styles.empty, children: (0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Regular.AlarmClock, size: 36, color: THEME.TextDisabled }) }) }, 'pto-blank-' + index)) }) })
                ||
                    (0, jsx_runtime_1.jsx)(Loading_1.default, { loading: status == 'loading' }), isPrismHr &&
                (0, jsx_runtime_1.jsx)(PtoBalanceSummaryBreakdown_1.default, { isVisible: sheetVisible, onDismiss: () => setSheetVisible(false), balance: selectedBalance, balances: balances, navigation: props.navigation })] }));
};
function TimeAttendanceCard(props) {
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { maintenanceMode, maintenanceCheck } = (0, MaintenanceContext_1.useMaintenanceContext)();
    const { t } = (0, react_i18next_1.useTranslation)();
    const [timeAndAttendance] = react_1.default.useState(userContext.client?.features?.find((feature) => feature.name === 'time-and-attendance'));
    const disabled = timeAndAttendance == null || !timeAndAttendance?.isEnabled;
    const vendor = timeAndAttendance?.vendor?.toLowerCase();
    const opensExternalTa = vendor === 'kronos' || vendor === 'timeco';
    const openTimeAttendance = async () => {
        if (!opensExternalTa) {
            props.navigation.navigate('TimeAttendance');
            return;
        }
        try {
            await Helper_1.Helper.verifyLocationPermissions();
        }
        catch (e) { }
        switch (vendor) {
            case 'kronos': {
                FirebaseAnalytics_1.default.LogEvent('feature_used', {
                    feature: 'Kronos SSO',
                    client: userContext.client?.id,
                    employee: userContext.employee?.employeeId
                });
                SingleSignOn_1.SingleSignOn.Perform({
                    navigation: props.navigation,
                    client: userContext?.client?.id,
                    provider: 'kronos',
                    title: t('common:screenTitles.ta')
                });
                break;
            }
            case 'timeco': {
                FirebaseAnalytics_1.default.LogEvent('feature_used', {
                    feature: 'TimeCo SSO',
                    client: userContext.client?.id,
                    employee: userContext.employee?.employeeId
                });
                SingleSignOn_1.SingleSignOn.Perform({
                    navigation: props.navigation,
                    client: userContext?.client?.id,
                    provider: 'timeco',
                    title: t('common:screenTitles.ta')
                });
                break;
            }
        }
    };
    return ((0, jsx_runtime_1.jsx)(Card_1.default, { style: {
            backgroundColor: 'transparent',
            borderWidth: 0,
            borderBottomWidth: 0,
            padding: 0,
            marginTop: 12
        }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { margin: -3 }, children: [(0, jsx_runtime_1.jsx)(MainMenuItem_1.default, { disabled: disabled || (maintenanceCheck && maintenanceMode === "on"), icon: Theme_1.ICONS.Solid.AlarmClock, action_icon: opensExternalTa ? Theme_1.ICONS.Regular.ArrowUpRightFromSquare : undefined, text: timeAndAttendance?.vendor?.toLowerCase() == 'prismhr' ? t('common:screenTitles.timeoff') : t('common:screenTitles.ta'), style: { margin: 3, zIndex: 2 }, onPress: openTimeAttendance }), (disabled || (maintenanceCheck && maintenanceMode === "on")) || (0, jsx_runtime_1.jsx)(TimeClock, {}), ((disabled ||
                    timeAndAttendance?.vendor?.toLowerCase() != 'kronos' ||
                    timeAndAttendance?.showKronosPto)
                    && (maintenanceCheck && maintenanceMode !== "on"))
                    &&
                        (0, jsx_runtime_1.jsx)(PaidTimeOff, { disabled: disabled, navigation: props.navigation, onOpenTimeAttendance: openTimeAttendance, vendor: vendor })] }) }));
}
;
